// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — pages/Contabil/PlataformaContador.tsx
//  Plataforma Contador — empacotar e enviar documentos fiscais ao contador
//  (download ZIP / e-mail) + sincronização com a nuvem externa Painel Contador.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Download, Mail, Loader2, Info, Users, Cloud, Send, RefreshCw, FileText, ShieldCheck,
} from 'lucide-react';
import { AcessoContadorAba } from '@/features/contabil/components/AcessoContadorAba';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/hooks/useAuth';
import { usePermissoes } from '@/contexts/PermissoesContext';
import { FORMULARIOS } from '@/domain/formularios';
import { toast } from '@/components/ui/sonner';
import { plataformaContadorService as svc } from '@/features/contabil/services/plataformaContadorService';

const today = () => new Date().toISOString().slice(0, 10);
const firstDayOfMonth = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};
const fmtMoeda = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

interface FiltroState {
  dtInicio: string;
  dtFim: string;
  modelo: string;
  tipo: string;
}

function SituacaoBadge({ situacao }: { situacao: string }) {
  const variant = situacao === 'Autorizada' ? 'default'
    : situacao === 'Cancelada' ? 'destructive' : 'secondary';
  return <Badge variant={variant}>{situacao}</Badge>;
}

const PlataformaContador = () => {
  const { user } = useAuth();
  const empresaId = user?.empresa?.id ?? 0;
  const { temPermissao } = usePermissoes();
  const podeEnviar = temPermissao(FORMULARIOS.PLATAFORMA_CONTADOR, 'incluir');

  const [filtro, setFiltro] = useState<FiltroState>({
    dtInicio: firstDayOfMonth(), dtFim: today(), modelo: '0', tipo: '-1',
  });
  // filtro efetivamente aplicado (só muda ao clicar Buscar)
  const [aplicado, setAplicado] = useState<FiltroState>(filtro);
  const [incluirPdf, setIncluirPdf] = useState(true);
  const [incluirSintegra, setIncluirSintegra] = useState(false);
  const [incluirInutilizados, setIncluirInutilizados] = useState(true);
  const [emailOverride, setEmailOverride] = useState('');
  const [loadingZip, setLoadingZip] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingPainel, setLoadingPainel] = useState<string | null>(null);

  const reqBase = () => ({
    empresaId,
    dtInicio: aplicado.dtInicio,
    dtFim: aplicado.dtFim,
    modelo: Number(aplicado.modelo),
    tipo: Number(aplicado.tipo),
  });

  const contadorQuery = useQuery({
    queryKey: ['plataforma-contador', 'contador', empresaId],
    queryFn: () => svc.obterContador(empresaId),
    enabled: empresaId > 0,
  });

  const docsQuery = useQuery({
    queryKey: ['plataforma-contador', 'documentos', empresaId, aplicado],
    queryFn: () => svc.listarDocumentos({
      empresaId, dtInicio: aplicado.dtInicio, dtFim: aplicado.dtFim,
      modelo: Number(aplicado.modelo), tipo: Number(aplicado.tipo),
    }),
    enabled: empresaId > 0,
  });

  const docs = docsQuery.data ?? [];
  const totalDocs = docs.length;
  const totalValor = docs.reduce((s, d) => s + d.valorTotal, 0);
  const semXml = docs.filter((d) => !d.temXml).length;

  const buscar = () => setAplicado(filtro);

  const handleBaixarZip = async () => {
    setLoadingZip(true);
    try {
      await svc.baixarZip({ ...reqBase() });
      toast.success('ZIP gerado com sucesso.');
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const msg = (err as any)?.response?.data?.mensagem ?? 'Nenhum documento com XML no período.';
      toast.error('Erro ao gerar ZIP', { description: msg });
    } finally {
      setLoadingZip(false);
    }
  };

  const handleEnviarEmail = async () => {
    setLoadingEmail(true);
    try {
      const r = await svc.enviarEmail({
        ...reqBase(),
        incluirRelatorioPdf: incluirPdf,
        incluirSintegra,
        incluirInutilizados,
        emailDestino: emailOverride.trim() || null,
      });
      if (r.emailEnviado) {
        toast.success('Pacote enviado', { description: r.mensagem ?? '' });
      } else {
        toast.error('Falha no envio', { description: r.mensagem ?? '' });
      }
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const msg = (err as any)?.response?.data?.mensagem ?? 'Erro ao enviar o pacote ao contador.';
      toast.error('Erro', { description: msg });
    } finally {
      setLoadingEmail(false);
    }
  };

  const runPainel = async (acao: string, fn: () => Promise<{ sucesso: boolean; mensagem: string | null }>) => {
    setLoadingPainel(acao);
    try {
      const r = await fn();
      if (r.sucesso) toast.success('Painel Contador', { description: r.mensagem ?? 'Concluído.' });
      else toast.error('Painel Contador', { description: r.mensagem ?? 'Falhou.' });
      contadorQuery.refetch();
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const msg = (err as any)?.response?.data?.mensagem ?? 'Erro na sincronização com a nuvem.';
      toast.error('Painel Contador', { description: msg });
    } finally {
      setLoadingPainel(null);
    }
  };

  if (!empresaId) {
    return (
      <div className="p-6">
        <Alert><Info className="h-4 w-4" />
          <AlertDescription>Empresa não identificada. Faça login novamente.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const contador = contadorQuery.data;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Plataforma Contador</h1>
        <p className="text-muted-foreground mt-1">
          Empacote e envie os documentos fiscais ao seu contador.
        </p>
      </div>

      {/* Card do contador */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" /> Contador
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          {contadorQuery.isLoading ? <Skeleton className="h-6 w-64" /> : contador?.contadorConfigurado ? (
            <div className="flex flex-wrap gap-x-8 gap-y-1">
              <span><b>{contador.contadorNome}</b></span>
              <span className="text-muted-foreground">{contador.contadorCnpjCpf}</span>
              <span className="text-muted-foreground">{contador.contadorEmail}</span>
            </div>
          ) : (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Nenhum contador com e-mail configurado para esta empresa. Configure o contador
                no cadastro da empresa, ou informe um e-mail manualmente abaixo ao enviar.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="pacote">
        <TabsList>
          <TabsTrigger value="pacote" className="gap-2"><FileText className="h-4 w-4" />Pacote / E-mail</TabsTrigger>
          <TabsTrigger value="acesso" className="gap-2"><ShieldCheck className="h-4 w-4" />Acesso do contador</TabsTrigger>
          <TabsTrigger value="nuvem" className="gap-2"><Cloud className="h-4 w-4" />Painel Contador (nuvem)</TabsTrigger>
        </TabsList>

        {/* ── Tab Acesso do contador (Portal do Contador) ── */}
        <TabsContent value="acesso">
          <AcessoContadorAba podeAlterar={podeEnviar} sugestao={{
            email: contador?.contadorEmail, nome: contador?.contadorNome, cnpj: contador?.contadorCnpjCpf,
          }} />
        </TabsContent>

        {/* ── Tab Pacote/E-mail ── */}
        <TabsContent value="pacote" className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Filtro do período</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
                <div className="space-y-1">
                  <Label>Data inicial</Label>
                  <Input type="date" value={filtro.dtInicio}
                    onChange={(e) => setFiltro({ ...filtro, dtInicio: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label>Data final</Label>
                  <Input type="date" value={filtro.dtFim}
                    onChange={(e) => setFiltro({ ...filtro, dtFim: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label>Modelo</Label>
                  <Select value={filtro.modelo} onValueChange={(v) => setFiltro({ ...filtro, modelo: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Todos</SelectItem>
                      <SelectItem value="55">NF-e (55)</SelectItem>
                      <SelectItem value="65">NFC-e (65)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Sentido</Label>
                  <Select value={filtro.tipo} onValueChange={(v) => setFiltro({ ...filtro, tipo: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="-1">Todos</SelectItem>
                      <SelectItem value="1">Saída</SelectItem>
                      <SelectItem value="0">Entrada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={buscar} className="gap-2">
                  <RefreshCw className="h-4 w-4" /> Buscar
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Ações */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-2 rounded-lg border p-4">
              <p className="text-sm font-medium">Incluir no e-mail ao contador</p>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={incluirPdf} onCheckedChange={(v) => setIncluirPdf(Boolean(v))} />
                  Relatório PDF
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={incluirSintegra} onCheckedChange={(v) => setIncluirSintegra(Boolean(v))} />
                  Arquivo Sintegra
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={incluirInutilizados} onCheckedChange={(v) => setIncluirInutilizados(Boolean(v))} />
                  Numeração inutilizada
                </label>
              </div>
              <div className="space-y-1 mt-1">
                <Label className="text-xs">E-mail destino (opcional — sobrescreve o do contador)</Label>
                <Input type="email" placeholder={contador?.contadorEmail ?? 'contador@exemplo.com'}
                  value={emailOverride} onChange={(e) => setEmailOverride(e.target.value)} className="w-72" />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button variant="outline" onClick={handleBaixarZip} disabled={loadingZip || totalDocs === 0} className="gap-2">
                {loadingZip ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Baixar ZIP de XMLs
              </Button>
              <Button onClick={handleEnviarEmail} disabled={!podeEnviar || loadingEmail || totalDocs === 0} className="gap-2">
                {loadingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                Enviar ao contador
              </Button>
            </div>
          </div>

          {/* Resumo + grade */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Documentos do período {totalDocs > 0 && (
                  <span className="text-muted-foreground font-normal text-sm">
                    — {totalDocs} doc(s), total {fmtMoeda(totalValor)}
                    {semXml > 0 && ` · ${semXml} sem XML`}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {docsQuery.isLoading ? <Skeleton className="h-40" /> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nota</TableHead>
                      <TableHead>Série</TableHead>
                      <TableHead>Modelo</TableHead>
                      <TableHead>Sentido</TableHead>
                      <TableHead>Emissão</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                      <TableHead>Situação</TableHead>
                      <TableHead className="text-center">XML</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {docs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                          Nenhum documento no período. Ajuste o filtro e clique em Buscar.
                        </TableCell>
                      </TableRow>
                    ) : docs.map((d) => (
                      <TableRow key={d.operacaoId}>
                        <TableCell className="font-mono">{d.numeroNota}</TableCell>
                        <TableCell>{d.serie}</TableCell>
                        <TableCell>{d.modelo}</TableCell>
                        <TableCell>{d.sentido}</TableCell>
                        <TableCell>{new Date(d.dtEmissao).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell className="max-w-48 truncate">{d.clienteNome}</TableCell>
                        <TableCell className="text-right">{fmtMoeda(d.valorTotal)}</TableCell>
                        <TableCell><SituacaoBadge situacao={d.situacao} /></TableCell>
                        <TableCell className="text-center">
                          {d.temXml ? <Badge variant="outline">✓</Badge>
                            : <Badge variant="secondary">—</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab Nuvem ── */}
        <TabsContent value="nuvem" className="space-y-6">
          <Alert>
            <Cloud className="h-4 w-4" />
            <AlertDescription>
              Sincroniza o contador e a empresa com a plataforma externa <b>Painel Contador</b> e
              envia os XMLs do período para a nuvem. Requer credenciais de sistema configuradas
              (seção <code>PainelContador</code>).
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Estado da sincronização</CardTitle>
              <CardDescription>
                {contador?.painelConfigurado
                  ? `Contador na nuvem: ${contador.painelIdContador}${contador.painelIdEmpresa ? ` · Empresa: ${contador.painelIdEmpresa}` : ''}`
                  : 'Contador ainda não sincronizado com a nuvem.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button variant="outline" disabled={!podeEnviar || loadingPainel !== null}
                onClick={() => runPainel('contador', () => svc.sincronizarContador(empresaId))} className="gap-2">
                {loadingPainel === 'contador' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
                1. Sincronizar contador
              </Button>
              <Button variant="outline" disabled={!podeEnviar || loadingPainel !== null || !contador?.painelConfigurado}
                onClick={() => runPainel('cliente', () => svc.sincronizarCliente(empresaId))} className="gap-2">
                {loadingPainel === 'cliente' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
                2. Sincronizar empresa
              </Button>
              <Button disabled={!podeEnviar || loadingPainel !== null}
                onClick={() => runPainel('notas', () => svc.enviarNotasPainel({
                  empresaId, dtInicio: aplicado.dtInicio, dtFim: aplicado.dtFim,
                  modelo: Number(aplicado.modelo), tipo: Number(aplicado.tipo),
                }))} className="gap-2">
                {loadingPainel === 'notas' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                3. Enviar notas do período ({aplicado.dtInicio} a {aplicado.dtFim})
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PlataformaContador;
